<template>
    <!-- The host focuses this section after a completed step removes its triggering button; otherwise focus
         falls back to the document body.
         -->
    <section ref="root" class="summary" aria-label="Coaching setup" tabindex="-1">
        <h1 class="sr-only">Coaching setup</h1>

        <div class="layout" :class="{ 'is-complete': isComplete }">
            <div class="argument">
                <!-- Before calendar data arrives, the headline must not claim knowledge of the user's
                     schedule or meeting counts.
                     -->
                <p class="headline">{{ headline }}</p>
                <p class="lede">{{ lede }}</p>

                <BaseButton v-if="nextStep" theme="primary" class="cta" @click="emit('open', nextStep.id)">{{ ctaLabel }}</BaseButton>
            </div>

            <!-- What the setup was for, once it is all done: the readings it produced,
                 each with its own way into a conversation about it. Shown here rather
                 than described, because a finished checklist is a receipt and these are
                 the thing the receipt was for.

                 Readings are written by a calendar scan, so a finished setup does not
                 guarantee any: the four asks are satisfied from artifacts that outlive
                 the run, and a user can arrive here with all four done and nothing
                 scanned. That case is not an error and gets no apology -- the composer
                 below is the offer, and it is the offer either way. -->
            <section v-if="isComplete && readingsLoading" class="payoff" aria-busy="true">
                <h2 class="payoff-title">Working out where I’d start</h2>
                <ReadingSkeletonCard :index="0" />
            </section>

            <section v-else-if="isComplete && readings.length > 0" class="payoff">
                <h2 class="payoff-title">Here’s where I’d start</h2>
                <BaseCarousel v-model="shownReading" :item-ids="readingIds" label="Your readings" item-label="reading" animated show-count fit-active class="payoff-carousel">
                    <template v-for="reading in readings" #[`item-${reading.id}`] :key="reading.id">
                        <ReadingCard :reading="reading" interactive />
                    </template>
                </BaseCarousel>
            </section>

            <section v-if="isComplete && chatInput !== null" class="composer">
                <!-- Without readings, the composer's placeholder supplies the prompt; another heading would
                     duplicate it.
                     -->
                <h2 v-if="readings.length > 0" class="composer-title">Something else on your mind?</h2>
                <HomeChatInput
                    :document-upload-enabled="chatInput.document_upload_enabled"
                    :default-chat-redirect-url="chatInput.default_chat_redirect_url"
                    :pii-patterns="chatInput.pii_patterns"
                    :better-voice-composer-enabled="chatInput.better_voice_composer_enabled"
                    :has-workiq-connected="chatInput.has_workiq_connected" />
            </section>

            <!-- Rows are noninteractive to keep the next-step button as the single entry point. Hide the
                 completed checklist to focus attention on readings.
                 -->
            <ol v-if="!isComplete" class="index">
                <li v-for="(step, index) in steps" :key="step.id" class="entry" :class="{ 'is-done': step.done, 'is-current': step.id === nextStep?.id }">
                    <div class="entry-row">
                        <span aria-hidden="true" class="entry-index">
                            <i v-if="step.done" class="bi bi-check-lg" aria-hidden="true"></i>
                            <template v-else>{{ index + 1 }}</template>
                        </span>

                        <span class="entry-name">{{ step.label }}</span>

                        <!-- Use action labels instead of durations so each step emphasizes its benefit
                             rather than its time cost.
                             -->
                        <span class="entry-status">{{ step.done ? step.done_action : step.action }}</span>

                        <span class="entry-payoff">{{ copyFor(step).payoff }}</span>
                    </div>
                </li>
            </ol>
        </div>
    </section>
</template>

<script setup lang="ts">
/**
 * context_snapshot in calendar_first/setup.py shares completion artifacts with SetupStep.done. Avoid
 * knowledge percentages: no total is defined. Serif denotes Nadia's speech; sans denotes interface controls
 * and counts.
 */
import ReadingCard from "~vue/CalendarFirstOnboarding/ReadingCard.vue";
import ReadingSkeletonCard from "~vue/CalendarFirstOnboarding/ReadingSkeletonCard.vue";
import type { OnboardingReading } from "~vue/CalendarFirstOnboarding/types";
import HomeChatInput from "~vue/ChatHome/HomeChatInput.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCarousel from "~vue/components/BaseCarousel.vue";
import { SETUP_ASK_COPY, type SetupAskCopy, setupCtaLabel } from "~vue/ProfileSetup/setupAsks";
import type { ChatInputProps, ContextSnapshot, SetupStep, SetupStepId } from "~vue/ProfileSetup/types";
import { computed, ref, useTemplateRef, watch } from "vue";

const {
    steps,
    context,
    completedCount,
    readings = [],
    readingsLoading = false,
    chatInput = null,
} = defineProps<{
    steps: SetupStep[];
    context: ContextSnapshot;
    completedCount: number;
    /**
     * Empty during loading and before setup completes; an empty array alone does not establish that no
     * readings exist.
     */
    readings?: OnboardingReading[];
    /** True while a reading could still arrive, so the finished state waits rather than claiming there are none. */
    readingsLoading?: boolean;
    /** Null for hosts without chat configuration, including the dev gallery. */
    chatInput?: ChatInputProps | null;
}>();

/* The host hands focus back here when an ask closes. Exposed as a method rather than
   left to `$el`, which on this component is the leading comment of a fragment root. */
const root = useTemplateRef<HTMLElement>("root");
defineExpose({ focus: () => root.value?.focus({ preventScroll: true }) });

const emit = defineEmits<{ open: [SetupStepId] }>();

const nextStep = computed(() => steps.find((step) => !step.done) ?? null);
const isComplete = computed(() => nextStep.value === null);

const readingIds = computed(() => readings.map((reading) => reading.id));
const shownReading = ref("");
/* The host polls for readings after mount, so setup-time slide selection would miss them. */
watch(readingIds, (ids) => {
    if (shownReading.value === "" && ids.length > 0) {
        shownReading.value = ids[0];
    }
});

function copyFor(step: SetupStep): SetupAskCopy {
    return SETUP_ASK_COPY[step.id];
}

/* Shared with the homepage card that promotes this page, so the two cannot name the
   same next ask differently. */
const ctaLabel = computed(() => setupCtaLabel(steps, completedCount));

const headline = computed(() => {
    if (nextStep.value === null) {
        return "Thank you — now I know you.";
    }
    return completedCount === 0 ? "I can only coach the week I can see." : "There’s still part of your week I can’t see.";
});

const KNOWN_AS: Record<string, string> = {
    company: "your company",
    you: "how you work",
    team: "who you work with",
};

function nameOf(id: string): string {
    return KNOWN_AS[id] ?? id;
}

/*
 * Negative lists need "or" so each item is independently unknown; "and" could imply only their combination
 * is unknown.
 */
function sentenceList(parts: string[], conjunction: string): string {
    if (parts.length <= 1) {
        return parts[0] ?? "";
    }
    return `${parts.slice(0, -1).join(", ")} ${conjunction} ${parts.at(-1)}`;
}

const lede = computed(() => {
    if (nextStep.value === null) {
        /* Setup can complete without a scan, producing no readings to link to. */
        const close = readings.length > 0 ? "Let’s put them to work — pick up one of these, or start anywhere you like." : "Let’s put them to work. Tell me what’s going on.";
        return `Your week, your working style and your own words are all in front of me now. ${close}`;
    }
    if (completedCount === 0) {
        return "My advice gets better the more I know about you. Set up a few things now and start getting insights tailored to you.";
    }
    const missing = context.dimensions.filter((dimension) => dimension.value <= 0).map((dimension) => nameOf(dimension.id));
    const gap = missing.length === 0 ? "There are gaps left in what I have." : `I still don’t know ${sentenceList(missing, "or")}.`;
    return `${gap} What’s beside this closes them.`;
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .summary {
        @apply w-full;
    }

    /* Keep the introduction above the checklist at every breakpoint to preserve their reading order. */
    .layout {
        @apply flex max-w-168 flex-col gap-10;
    }

    .layout.is-complete {
        @apply mx-auto items-center text-center;
    }

    /* This width is tuned for three balanced headline lines without widows. */
    .headline {
        @apply display-medium max-w-[11em] text-ink;
        text-wrap: balance;
    }

    .lede {
        @apply nadia-chat mt-5 max-w-[40ch] text-ink-muted;
    }

    .is-complete .headline,
    .is-complete .lede {
        @apply mx-auto;
    }

    .is-complete .payoff {
        @apply w-full text-left;
    }

    .payoff-carousel {
        @apply w-full;
    }

    .is-complete .composer {
        @apply w-full text-left;
    }

    .cta {
        @apply mt-7 self-start;
    }

    .payoff {
        @apply flex flex-col gap-4;
    }

    .payoff-title {
        @apply nadia-subtitle text-ink;
    }

    .composer {
        @apply flex flex-col gap-3;
    }

    .composer-title {
        @apply nadia-subtitle text-ink;
    }

    .index {
        @apply flex flex-col;
    }

    .entry {
        @apply border-b border-border first:border-t;
    }

    .entry-row {
        @apply grid grid-cols-[1rem_minmax(0,1fr)_auto] items-baseline gap-x-3.5 py-4 text-left;
    }

    .entry-index {
        @apply nadia-subtitle-small text-right tabular-nums text-ink-muted;
    }

    .is-done .entry-index {
        @apply text-status-success;
    }

    .entry-name {
        @apply nadia-subtitle-small min-w-0 text-ink;
    }

    .entry-status {
        @apply body-small shrink-0 text-ink-muted;
    }

    .entry-payoff {
        @apply nadia-chat col-start-2 col-end-4 mt-1 text-ink-muted;
    }

    .is-done .entry-name {
        @apply text-ink-muted;
    }
}
</style>
